import { useMemo, useState } from "react";
import { Card, Radio } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const BASES = [
  { key: "bin", radix: 2, group: 4, validRe: /^[01]+$/ },
  { key: "oct", radix: 8, group: 0, validRe: /^[0-7]+$/ },
  { key: "dec", radix: 10, group: 0, validRe: /^[0-9]+$/ },
  { key: "hex", radix: 16, group: 0, validRe: /^[0-9a-fA-F]+$/ },
];

const WIDTHS = [
  { key: "auto", bits: 0 },
  { key: "8", bits: 8 },
  { key: "16", bits: 16 },
  { key: "32", bits: 32 },
  { key: "64", bits: 64 },
];

const BASE_LABEL_KEYS = {
  bin: "number_base_bin_label",
  oct: "number_base_oct_label",
  dec: "number_base_dec_label",
  hex: "number_base_hex_label",
};

const BASE_PLACEHOLDER_KEYS = {
  bin: "number_base_bin_placeholder",
  oct: "number_base_oct_placeholder",
  dec: "number_base_dec_placeholder",
  hex: "number_base_hex_placeholder",
};

const stripPrefix = (raw, baseKey) => {
  const trimmed = raw.trim().replace(/_/g, "");
  if (baseKey === "hex" && /^0x/i.test(trimmed)) return trimmed.slice(2);
  if (baseKey === "bin" && /^0b/i.test(trimmed)) return trimmed.slice(2);
  if (baseKey === "oct" && /^0o/i.test(trimmed)) return trimmed.slice(2);
  return trimmed;
};

const groupBinary = (bits) => {
  if (!bits) return "";
  const padded = bits.length % 4 === 0 ? bits : "0".repeat(4 - (bits.length % 4)) + bits;
  return padded.match(/.{1,4}/g).join(" ");
};

const formatForBase = (value, baseKey, fixedBits) => {
  if (value === null) return "";
  const base = BASES.find((b) => b.key === baseKey);
  let text = value.toString(base.radix);
  if (baseKey === "hex") text = text.toUpperCase();
  if (baseKey === "bin" && fixedBits > 0) {
    text = text.padStart(fixedBits, "0").slice(-fixedBits);
  }
  return text;
};

// Returns null when the sign bit isn't set — signed == unsigned, no new info to show.
const signedInterpretation = (value, widthBits) => {
  if (value === null || widthBits <= 0) return null;
  const signBit = 1n << BigInt(widthBits - 1);
  if ((value & signBit) === 0n) return null;
  const modulus = 1n << BigInt(widthBits);
  return (value - modulus).toString(10);
};

const NumberBaseConverter = () => {
  const { t } = useTranslation();
  const [activeBase, setActiveBase] = useState("dec");
  const [rawInput, setRawInput] = useState("255");
  const [width, setWidth] = useState("auto");

  const widthBits = useMemo(
    () => WIDTHS.find((w) => w.key === width)?.bits ?? 0,
    [width],
  );

  const { value, error } = useMemo(() => {
    if (rawInput.trim() === "") return { value: null, error: null };
    const baseDef = BASES.find((b) => b.key === activeBase);
    const cleaned = stripPrefix(rawInput, activeBase);
    if (cleaned === "") return { value: null, error: null };
    if (!baseDef.validRe.test(cleaned)) {
      return { value: null, error: "number_base_error_invalid" };
    }
    const prefix =
      activeBase === "hex" ? "x" : activeBase === "oct" ? "o" : activeBase === "bin" ? "b" : "";
    let parsed;
    try {
      parsed = BigInt(`0${prefix}${cleaned}`);
    } catch {
      return { value: null, error: "number_base_error_invalid" };
    }
    if (widthBits > 0) {
      const max = (1n << BigInt(widthBits)) - 1n;
      if (parsed > max) {
        return { value: null, error: "number_base_error_overflow" };
      }
    }
    return { value: parsed, error: null };
  }, [rawInput, activeBase, widthBits]);

  const outputs = useMemo(() => {
    const result = {};
    BASES.forEach((b) => {
      result[b.key] = formatForBase(value, b.key, b.key === "bin" ? widthBits : 0);
    });
    return result;
  }, [value, widthBits]);

  const onBaseChange = (nextKey) => {
    if (nextKey === activeBase) return;
    if (value !== null && !error) {
      setRawInput(outputs[nextKey]);
    }
    setActiveBase(nextKey);
  };

  const copy = async (text) => {
    if (!text) {
      toast.error(t("nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const clear = () => setRawInput("");

  const binaryGrouped = value !== null ? groupBinary(outputs.bin) : "";
  const signedDecimal = useMemo(
    () => signedInterpretation(value, widthBits),
    [value, widthBits],
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-6">
            <div>
              <h1 className="text-2xl font-bold text-indigo-700">
                {t("number_base_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("number_base_intro")}</p>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("number_base_input_base_label")}
                </label>
                <Radio.Group
                  value={activeBase}
                  onChange={(e) => onBaseChange(e.target.value)}
                >
                  {BASES.map((b) => (
                    <Radio.Button key={b.key} value={b.key}>
                      {t(BASE_LABEL_KEYS[b.key])}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("number_base_width_label")}
                </label>
                <Radio.Group
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                >
                  {WIDTHS.map((w) => (
                    <Radio.Button key={w.key} value={w.key}>
                      {t(`number_base_width_${w.key}`)}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                {t("number_base_input_label", { base: t(BASE_LABEL_KEYS[activeBase]) })}
              </label>
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 focus:outline-none font-mono text-sm"
                placeholder={t(BASE_PLACEHOLDER_KEYS[activeBase])}
                value={rawInput}
                onChange={(e) => setRawInput(e.target.value)}
                autoComplete="off"
                spellCheck="false"
              />
              <div className="flex flex-wrap gap-3">
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("number_base_clear")}
                </StandardButton>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600">{t(error)}</p>
            )}

            {!error && value !== null && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {BASES.map((b) => (
                  <div
                    key={b.key}
                    className="bg-indigo-50 p-4 rounded-md border border-indigo-200"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <strong className="text-indigo-700">
                        {t(BASE_LABEL_KEYS[b.key])}
                      </strong>
                      <StandardButton
                        onClick={() => copy(b.key === "bin" ? binaryGrouped : outputs[b.key])}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="break-all mt-2 text-gray-800 font-mono text-sm whitespace-pre-wrap">
                      {b.key === "bin" ? binaryGrouped : outputs[b.key]}
                    </p>
                    {b.key === "dec" && signedDecimal !== null && (
                      <div className="mt-2 flex items-center justify-between gap-2 border-t border-indigo-100 pt-2">
                        <span className="text-xs text-indigo-700 font-mono break-all">
                          {t("number_base_signed_hint", {
                            bits: widthBits,
                            value: signedDecimal,
                          })}
                        </span>
                        <StandardButton
                          onClick={() => copy(signedDecimal)}
                          type="secondary"
                          variant="outlined"
                          size="small"
                        >
                          {t("copy_button")}
                        </StandardButton>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("number_base_note_prefix")}</p>
              <p>{t("number_base_note_bigint")}</p>
              <p>{t("number_base_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default NumberBaseConverter;
